import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";

/* ── Ambient soundscape ────────────────────────────────────────────────
   Original, non-copyrighted evolving pad synthesised for The Thriving Life 101.
   To swap in a professionally-composed signature sound later, change this one line. */
const AMBIENT_SRC = "/audio/soundscape-v1.mp3";
const SOUND_PREF_KEY = "ttl101-ambient-pref";

/* Cinematic hero films (commercially-licensed placeholders to lock the feeling) */
const FILMS = {
  forest: { src: "/video/forest-web.mp4", poster: "/video/forest-poster.jpg" },
  water: { src: "/video/water-web.mp4", poster: "/video/water-poster.jpg" },
  sky: { src: "/video/sky-web.mp4", poster: "/video/sky-poster.jpg" },
};

/* Opt-in ambient sound. Never autoplays on first visit. Smooth fades. Remembers session preference. */
function AmbientSound({ light = false, className = "", volume = 0.5 }) {
  const audioRef = useRef(null);
  const rampRef = useRef(null);
  const [on, setOn] = useState(false);

  const ramp = (to, done) => {
    clearInterval(rampRef.current);
    const a = audioRef.current;
    if (!a) return;
    rampRef.current = setInterval(() => {
      let v = a.volume;
      v = to > v ? Math.min(to, v + 0.012) : Math.max(to, v - 0.012);
      a.volume = v;
      if (Math.abs(v - to) < 0.001) { a.volume = to; clearInterval(rampRef.current); done && done(); }
    }, 55);
  };

  const start = () => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0;
    a.play().then(() => { setOn(true); ramp(volume); }).catch(() => {});
  };
  const stop = () => { const a = audioRef.current; if (!a) return; setOn(false); ramp(0, () => a.pause()); };

  const toggle = () => {
    if (!on) { start(); sessionStorage.setItem(SOUND_PREF_KEY, "on"); }
    else { stop(); sessionStorage.setItem(SOUND_PREF_KEY, "off"); }
  };

  useEffect(() => {
    if (sessionStorage.getItem(SOUND_PREF_KEY) === "on") start();
    return () => clearInterval(rampRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tone = light ? "text-white/60 hover:text-white/95" : "text-secondary-custom hover:text-primary-custom";
  const dot = on ? "bg-[var(--accent-bronze)]" : light ? "bg-white/45" : "bg-secondary-custom/45";
  return (
    <>
      <audio ref={audioRef} src={AMBIENT_SRC} loop preload="auto" data-testid="ambient-audio" />
      <button onClick={toggle} data-testid="sound-toggle" aria-pressed={on}
        className={`group inline-flex items-center gap-2.5 font-sans text-[9px] uppercase tracking-[0.4em] transition-colors duration-700 ${tone} ${className}`}>
        <span className={`inline-block w-[5px] h-[5px] rounded-full transition-colors duration-700 ${dot} ${on ? "animate-pulse" : ""}`} />
        <span>{on ? "Sound On" : "Sound Off"}</span>
      </button>
    </>
  );
}

function HeroVideo({ film, overlay }) {
  return (
    <>
      <motion.video
        initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ opacity: { duration: 3, ease: "easeOut" }, scale: { duration: 18, ease: "easeOut" } }}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay muted loop playsInline preload="auto" poster={film.poster}
        onCanPlay={(e) => { const p = e.target.play(); if (p && p.catch) p.catch(() => {}); }}
      >
        <source src={film.src} type="video/mp4" />
      </motion.video>
      <div className={`absolute inset-0 ${overlay}`} />
    </>
  );
}

const easeSoft = [0.16, 1, 0.3, 1];
const rise = (delay = 0, y = 18) => ({
  initial: { opacity: 0, y }, animate: { opacity: 1, y: 0 },
  transition: { duration: 2, delay, ease: easeSoft },
});

/* CONCEPT A — LIGHT & NATURE (forest) · centered, cinematic */
function ConceptA() {
  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      <HeroVideo film={FILMS.forest} overlay="bg-black/40 [background:radial-gradient(ellipse_at_center,transparent_25%,rgba(0,0,0,0.55)_100%)]" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <motion.span {...rise(1)} className="font-sans text-[10px] uppercase tracking-[0.5em] text-white/70 mb-9">The Thriving Life 101</motion.span>
        <motion.h1 {...rise(1.8, 22)} className="font-serif font-light text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-[-0.01em] max-w-3xl">
          The architecture<br />of a <span className="italic">well-lived</span> life.
        </motion.h1>
      </div>
      <motion.div {...rise(3)} className="absolute bottom-9 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <Link to="/start-here" data-testid="entry-cue" className="font-sans text-[10px] uppercase tracking-[0.4em] text-white/80 hover:text-white transition-colors duration-500">Begin</Link>
        <span className="block w-px h-9 bg-white/30" />
      </motion.div>
      <div className="fixed top-14 right-6 sm:right-10 z-30"><AmbientSound light className="py-2.5 px-3" /></div>
    </section>
  );
}

/* CONCEPT B — WATER & MOVEMENT · bottom-left editorial */
function ConceptB() {
  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      <HeroVideo film={FILMS.water} overlay="bg-gradient-to-t from-black/75 via-black/15 to-black/30" />
      <div className="relative z-10 h-full flex flex-col justify-end px-6 sm:px-12 lg:px-20 pb-20 sm:pb-24">
        <motion.span {...rise(1)} className="font-sans text-[10px] uppercase tracking-[0.5em] text-white/70 mb-6">The Thriving Life 101</motion.span>
        <motion.h1 {...rise(1.8, 22)} className="font-serif font-light text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-[-0.01em] max-w-3xl">
          The architecture of a<br /><span className="italic">well-lived</span> life.
        </motion.h1>
        <motion.div {...rise(3)} className="mt-9">
          <Link to="/start-here" data-testid="entry-cue" className="font-sans text-[10px] uppercase tracking-[0.4em] text-white border-b border-white/40 pb-1.5 hover:border-white transition-colors duration-500">Begin</Link>
        </motion.div>
      </div>
      <div className="fixed top-14 right-6 sm:right-10 z-30"><AmbientSound light className="py-2.5 px-3" /></div>
    </section>
  );
}

/* CONCEPT C — SKY & AIR · high, airy, restrained */
function ConceptC() {
  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      <HeroVideo film={FILMS.sky} overlay="bg-black/25" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <motion.h1 {...rise(1.4, 22)} className="font-serif font-light text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-[-0.01em] max-w-3xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.35)]">
          The architecture<br />of a <span className="italic">well-lived</span> life.
        </motion.h1>
        <motion.span {...rise(2.6)} className="font-sans text-[10px] uppercase tracking-[0.5em] text-white/75 mt-10">A lifelong exploration</motion.span>
      </div>
      <div className="fixed top-14 right-6 sm:right-10 z-30"><AmbientSound light className="py-2.5 px-3" /></div>
    </section>
  );
}

export default function HeroPreview() {
  const { id } = useParams();
  const concept = { "1": <ConceptA />, "2": <ConceptB />, "3": <ConceptC /> }[id] || <ConceptA />;
  const labels = {
    "1": "Concept A — Light & Nature · forest, dappled light",
    "2": "Concept B — Water & Movement · reflection, flow",
    "3": "Concept C — Sky & Air · openness, calm",
  };
  return (
    <div>
      <div className="bg-black text-white text-center py-2.5 px-4 text-[10px] tracking-[0.3em] uppercase font-sans">
        {labels[id] || labels["1"]} · Hero film · Review only — not live
      </div>
      {concept}
      <div className="bg-surface-custom border-t border-subtle-custom py-6 flex flex-wrap items-center justify-center gap-8 text-[10px] tracking-[0.3em] uppercase font-sans">
        <Link to="/hero-preview/1" data-testid="nav-concept-1" className="hover:text-[var(--accent-bronze)] transition-colors">Concept A · Nature</Link>
        <Link to="/hero-preview/2" data-testid="nav-concept-2" className="hover:text-[var(--accent-bronze)] transition-colors">Concept B · Water</Link>
        <Link to="/hero-preview/3" data-testid="nav-concept-3" className="hover:text-[var(--accent-bronze)] transition-colors">Concept C · Sky</Link>
      </div>
    </div>
  );
}
